// components/ProtectedRoute.tsx

import { useAuth } from '../context/AuthContext';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { USER_ROLES } from '../data/models';

type ProtectedRouteProps = {
  children: React.ReactNode;
  allowedRoles?: string[]; // Optional prop to specify allowed roles
};

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { currentUser, loading } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;


  const isAuthenticated = !!currentUser;
  const isLoading = loading;
  const user = currentUser;

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        // If not authenticated, redirect to login
        navigate('/login');
      } else if (allowedRoles && user?.role && !allowedRoles.includes(user.role)) {
        console.log('User role not authorized for this route:', user.role);
        // If user doesn't have the required role, redirect to their dashboard or show unauthorized
        switch (user.role) {
          case USER_ROLES.CLIENT:
            navigate('/clientDashboard');
            break;
          case USER_ROLES.DATA_COLLECTOR:
            navigate('/dashboard');
            break;
          case USER_ROLES.SUPERVISOR:
            navigate('/supervisor-dashboard');
            break;
          case USER_ROLES.ADMIN:
            navigate('/admin-dashboard');
            break;
          default:
            navigate('/'); // Fallback to home or login
        }
      }
    }
  }, [isAuthenticated, isLoading, user, navigate, allowedRoles, pathname]);

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return null;
  }

  // Check if route is restricted by role
  if (allowedRoles && user?.role && !allowedRoles.includes(user.role)) {
    return <div className="flex items-center justify-center min-h-screen">Unaauthorized</div>;
  }

  return <>{children}</>;
}